import type { BasketLastItem, BasketResponse } from "@onestore/api/basket"
import type { AccountType } from "@onestore/api/types"
import type { JentisProductItem } from "@onestore/jentis-datalayer"
import { getCurrencyCode, getGA4Price, getGA4PriceWithTax } from "@core/pricing"
import type { BasketViewItem } from "@gatsby-plugin-basket/types"
import type { CloudBluePeriod } from "@gatsby-plugin-definitions/fragments/CloudBluePeriod"
import { globalHistory } from "@reach/router"
import { forEach, values } from "lodash"
import type { GaEvent } from "~/fragments/gaEvent"
import type {
  AiSearchEvent,
  CardItem,
  CmsObjectEvent,
  ContentShowEvent,
  EcommerceAddProductGA4DataLayer,
  EcommerceAddPromoCodeGA4DataLayer,
  EcommerceBasketBeginCheckoutGA4DataLayer,
  EcommerceBasketChangePaymentGA4DataLayer,
  EcommerceBasketShowGA4DataLayer,
  EcommerceOrderGA4DataLayer,
  EcommerceProductRemoveGA4DataLayer,
  EcommerceRemovePromoCodeGA4DataLayer,
  EcommerceSelectDomainGA4DataLayer,
  EcommerceSelectItemGA4DataLayer,
  ExperimentEvent,
  FaqShowEvent,
  MenuViewEvent,
  ProductBoxClickEvent,
  SiteSearchResultGa4DataLayer,
  SiteSearchResultStatus,
  ToplayerShowEvent,
  TransactionProduct,
  UserEvent,
} from "~/lib/dataLayer"
import { dataLayerPush } from "~/lib/dataLayer"
import isEmpty from "~/lib/isEmpty"
import type { TrackingContextType, TrackingItem } from "~/lib/tracking"
import { Tracking } from "~/lib/tracking"
import type { BasketActionSource } from "~/store/basket/constants"
import { BasketActions } from "~/store/basket/constants"
import type { BasketProductItem } from "~/store/basket/types"
import {
  getBasketItemIndex,
  getLastItemsFromBasket,
} from "~/store/basket/utils"
import { getAddCartSource } from "./basket"
import type { FormEvent } from "./dataLayer"
import {
  pushOAOAddItem,
  pushOAOAddVoucher,
  pushOAOBasketOrderItem,
  pushOAOEmptyBasket,
  pushOAORemoveItem,
  pushOAORemoveVoucher,
} from "./oao"

const ECOMMERCE_TRACK_ITEMS_WITHOUT_SKU = true

export enum EventCategory {
  FORM_VIEW = "form_view",
  FORM_SUBMIT = "form_submit",
  FORM_START = "form_start",
  ADD_TO_CART = "add_to_cart",
  ORDER = "purchase",
  CART = "view_cart",
  REMOVE_FROM_CART = "remove_from_cart",
  DOMAIN_SEARCH_FILTERS = "DomainSearchFilters",
}

const unknownTokenText = "unknownToken"

function sendJentisCmsObjectEvent(category: string, action: string): void {
  window.jentisDataLayer?.pushCmsObjectEvent(category, action)
}

export function sendGaEvent(gaEvent?: GaEvent | null): void {
  if (!isEmpty(gaEvent)) {
    dataLayerPush<CmsObjectEvent>({
      event: "cms_object",
      event_type: "ux_interaction",
      object_category: gaEvent.eventCategory,
      object_name: gaEvent.eventAction,
    })

    sendJentisCmsObjectEvent(gaEvent.eventCategory, gaEvent.eventAction)
  }
}

export function sendUserLoginEvent(_user_id: string | null): void {
  dataLayerPush<UserEvent>({
    event: "login",
    event_type: "user_interaction",
  })
}

export function sendUserRegisterEvent(): void {
  dataLayerPush<UserEvent>({
    event: "register",
    event_type: "user_interaction",
  })
}

export function sendMenuViewEvent() {
  dataLayerPush<MenuViewEvent>({
    event: "menu_view",
    event_type: "ux_interaction",
  })
}

export function sendBackToCartEvent() {
  dataLayerPush<CmsObjectEvent>({
    event: "cms_object",
    event_type: "ux_interaction",
    object_category: "checkout",
    object_name: "back_to_cart",
  })

  sendJentisCmsObjectEvent("checkout", "back_to_cart")
}

export function sendBackToShopEvent() {
  dataLayerPush<CmsObjectEvent>({
    event: "cms_object",
    event_type: "ux_interaction",
    object_category: "upsell_cart",
    object_name: "back_to_shop",
  })

  sendJentisCmsObjectEvent("upsell_cart", "back_to_shop")
}

export function sendNoDomainEvent() {
  dataLayerPush<CmsObjectEvent>({
    event: "cms_object",
    event_type: "ux_interaction",
    object_category: "upsell",
    object_name: "kontynuuj_bez_domeny",
  })

  sendJentisCmsObjectEvent("upsell", "kontynuuj_bez_domeny")
}

export function sendToBasketEvent() {
  dataLayerPush<CmsObjectEvent>({
    event: "cms_object",
    event_type: "ux_interaction",
    object_category: "upsell",
    object_name: "przechodze_do_koszyka",
  })

  sendJentisCmsObjectEvent("upsell", "przechodze_do_koszyka")
}

export function sendBonusContinueEvent() {
  dataLayerPush<CmsObjectEvent>({
    event: "cms_object",
    event_type: "ux_interaction",
    object_category: "upsell",
    object_name: "kontynuuj",
  })

  sendJentisCmsObjectEvent("upsell", "kontynuuj")
}

export function sendBonusGotoBasketEvent() {
  dataLayerPush<CmsObjectEvent>({
    event: "cms_object",
    event_type: "ux_interaction",
    object_category: "upsell",
    object_name: "idz_do_kasy",
  })

  sendJentisCmsObjectEvent("upsell", "idz_do_kasy")
}

export function userLogoutEvent() {
  dataLayerPush<CmsObjectEvent>({
    event: "cms_object",
    event_type: "ux_interaction",
    object_category: "menu",
    object_name: "logout",
  })

  sendJentisCmsObjectEvent("menu", "logout")
}

export function userMenuDropdownEvent() {
  dataLayerPush<CmsObjectEvent>({
    event: "cms_object",
    event_type: "ux_interaction",
    object_category: "menu",
    object_name: "user_dropdown",
  })

  sendJentisCmsObjectEvent("menu", "user_dropdown")
}

export function sendFaqShowEvent(faqSubject: string) {
  dataLayerPush<FaqShowEvent>({
    event: "faq_show",
    event_type: "ux_interaction",
    faq_subject: faqSubject,
  })
}

export function sendContentShowEvent(eventSource?: string): void {
  dataLayerPush<ContentShowEvent>({
    event: "content_show",
    event_type: "ux_interaction",
    eventSource: eventSource,
  })
}

export function sendToplayerShowEvent(toplayer_name?: string) {
  dataLayerPush<ToplayerShowEvent>({
    event: "toplayer_show",
    event_type: "ux_interaction",
    toplayer_name: toplayer_name || null,
  })
}

export function sendGAFormEvent({
  event,
  form_type,
  form_id,
}: Omit<FormEvent, "event_type" | "form_provider">): void {
  dataLayerPush<FormEvent>({
    event,
    event_type: "form_interaction",
    form_type: form_type || "contents",
    form_provider: "inner",
    form_id,
  })
}

export function sendSiteSearchResultEvent(
  search_term: string,
  status?: SiteSearchResultStatus
): void {
  dataLayerPush<SiteSearchResultGa4DataLayer>({
    event: "site_search_result",
    event_type: "site_search_interaction",
    search_term: search_term,
    status: status,
  })
}

export const getLastItemsProductsData = (
  items: BasketLastItem[],
  cart_id: string | null,
  basketIndex: number
): CardItem[] => {
  const result: Record<string, CardItem> = {}

  let index = 0

  const addItemToResult = (context: TrackingContextType) => {
    const { item, name, category } = context

    if (!item.sku && !ECOMMERCE_TRACK_ITEMS_WITHOUT_SKU) {
      return
    }

    result[index] = createTrackedItemGA4({
      ...item,
      name,
      category,
      index: basketIndex,
      cart_id,
    })

    index++
  }

  items.forEach((item) => {
    addItemToResult(Tracking.getTrackingContext(item))
  })

  return values(result)
}

export const getProductsGA4 = (
  items: (BasketProductItem | BasketViewItem)[],
  event: EventCategory | null = null,
  cart_id: string
): CardItem[] => {
  const result: Record<string, CardItem> = {}
  const onlyPatchedItems = event === EventCategory.ADD_TO_CART

  let basketIndex = 0

  const addItemToResult = (context: TrackingContextType) => {
    const { item, name, category } = context

    if (!item.sku && !ECOMMERCE_TRACK_ITEMS_WITHOUT_SKU) {
      return
    }

    if (onlyPatchedItems && !item.last_patch_operation_flag) {
      return
    }

    result[basketIndex] = createTrackedItemGA4({
      ...item,
      name,
      category,
      index: basketIndex,
      cart_id,
    })

    basketIndex++

    if ("children" in item && item.children.length > 0) {
      forEach<TrackingItem>(item.children, (childItem) => {
        addItemToResult(Tracking.getTrackingContext(childItem, item))
      })
    }
  }

  forEach(items, (item) => {
    addItemToResult(Tracking.getTrackingContext(item))
  })

  return values(result)
}

export const getBasketLastItemIndex = (items: BasketProductItem[]): number => {
  let index = 0

  const addItemToResult = (basketItem: BasketProductItem) => {
    index++

    if ("children" in basketItem && basketItem.children.length > 0) {
      forEach<BasketProductItem>(basketItem.children, (childItem) => {
        addItemToResult(childItem)
      })
    }
  }

  forEach(items, (item) => {
    addItemToResult(item)
  })

  return index
}

const createTrackedItemGA4 = (itemData): CardItem => {
  return {
    id: itemData.itemId ? itemData.itemId : itemData.id,
    item_id: itemData.sku ? itemData.sku : itemData.name,
    item_name: itemData.name,
    affiliation: globalHistory.location.hostname
      ? globalHistory.location.hostname
      : undefined,
    index: null,
    cart_index: itemData.index,
    currency: getCurrencyCode(),
    item_category: itemData.category,
    item_variant: itemData.period_name,
    price: getGA4Price(itemData.price) || 0,
    quantity: 1,
    cart_id: itemData.cart_id,
    product_plan_id: itemData.planId || itemData.plan_id || null,
  }
}

const getJentisConvertedProductsItems = (
  items: CardItem[]
): JentisProductItem[] =>
  items.map((item) => ({
    ...item,
    priceWithTax: getGA4PriceWithTax(item.price) || 0,
  }))

export const orderProceedEvent = (
  transaction_id: string,
  totalValue: number,
  basketOrderedItems: BasketViewItem[],
  basketToken: string,
  userRegistered: boolean,
  accountType: AccountType,
  paymentType: number | null,
  systemId?: string
) => {
  const basketItems = getProductsGA4(
    basketOrderedItems,
    EventCategory.ORDER,
    basketToken
  )

  dataLayerPush<EcommerceOrderGA4DataLayer>({
    event: "purchase",
    event_type: "ecommerce",
    ecommerce: {
      transaction_id,
      value: getGA4Price(totalValue) || 0,
      currency: getCurrencyCode(),
      payment_type: paymentType,
      system_id: systemId,
      items: basketItems,
      is_new_customer: userRegistered ? 1 : 0,
      customer_type: accountType === "person" ? "individual" : "company",
    },
  })

  pushOAOBasketOrderItem(basketItems)

  window.jentisDataLayer?.pushPurchaseEventAndItems({
    items: getJentisConvertedProductsItems(basketItems),
    value: getGA4Price(totalValue) || 0,
    valueWithTax: getGA4PriceWithTax(totalValue) || 0,
    currency: getCurrencyCode(),
    transaction_id,
    payment_type: paymentType,
    is_new_customer: userRegistered ? 1 : 0,
    customer_type: accountType === "person" ? "individual" : "company",
    system_id: systemId,
  })
}

export const basketClearItems = (
  cart_id: string,
  basketItems: BasketProductItem[]
) => {
  const orderedProducts = getProductsGA4(basketItems, null, cart_id)

  const items = orderedProducts.map((product) => {
    return {
      item_id: product.item_id,
      item_name: product.item_name,
      currency: getCurrencyCode(),
      price: product.price,
      quantity: product.quantity,
      index: null,
      cart_index: product.id
        ? getBasketItemIndex(orderedProducts, product.id)
        : undefined,
      affiliation: product.affiliation,
      item_category: product.item_category,
      item_variant: product.item_variant,
      cart_id: product.cart_id,
    }
  })

  dataLayerPush<EcommerceProductRemoveGA4DataLayer>({
    event: "remove_from_cart",
    event_type: "ecommerce",
    ecommerce: {
      items,
    },
  })

  pushOAOEmptyBasket(cart_id, items)

  window.jentisDataLayer?.pushRemoveFromCartEventAndItems(
    getJentisConvertedProductsItems(items)
  )
}

export const basketItemRemoved = (
  cart_id: string,
  basketOrderedItems: BasketProductItem[],
  id: number,
  item_id: string,
  item_name: string,
  price: number,
  quantity: number | undefined,
  affiliation: string | undefined,
  item_category: string | undefined,
  item_variant: string | undefined
) => {
  const orderedProducts = getProductsGA4(
    basketOrderedItems,
    EventCategory.REMOVE_FROM_CART,
    cart_id
  )
  const itemRemovedIndex = getBasketItemIndex(orderedProducts, id)

  const items = [
    {
      item_id,
      item_name,
      currency: getCurrencyCode(),
      price: getGA4Price(price) || 0,
      quantity,
      index: null,
      cart_index: itemRemovedIndex,
      ...(affiliation && {
        affiliation,
      }),
      ...(item_category && {
        item_category,
      }),
      ...(item_variant && {
        item_variant,
      }),
      ...(cart_id && {
        cart_id,
      }),
      product_plan_id: !isEmpty(orderedProducts[itemRemovedIndex])
        ? orderedProducts[itemRemovedIndex].product_plan_id
        : undefined,
    },
  ]

  dataLayerPush<EcommerceProductRemoveGA4DataLayer>({
    event: "remove_from_cart",
    event_type: "ecommerce",
    ecommerce: {
      items,
    },
  })

  pushOAORemoveItem(cart_id, id, basketOrderedItems)

  window.jentisDataLayer?.pushRemoveFromCartEventAndItems(
    getJentisConvertedProductsItems(items)
  )
}

export const basketAddItemsResultsEvent = (
  basket: BasketResponse,
  basketOrderedItems: BasketProductItem[],
  addToCartSource?: BasketActionSource
) => {
  const products: TransactionProduct[] = Tracking.getProducts(
    basket.items,
    EventCategory.ADD_TO_CART
  )

  const addedItems = getLastItemsFromBasket(basket)

  const addCartSource = getAddCartSource(addedItems) || addToCartSource

  if (products.length === 0) {
    return
  }

  const basketId = basket.token ? basket.token : null
  const basketItems = getLastItemsProductsData(
    addedItems,
    basket.token,
    getBasketLastItemIndex(basketOrderedItems)
  )

  dataLayerPush<EcommerceAddProductGA4DataLayer>({
    event: "add_to_cart",
    event_type: "ecommerce",
    cart_id: basketId,
    ecommerce: {
      add_to_cart_source: addCartSource,
      items: basketItems,
    },
  })

  pushOAOAddItem(basketItems, basketId)

  window.jentisDataLayer?.pushAddToCartEventAndItems({
    items: getJentisConvertedProductsItems(basketItems),
    basketId,
    currency: getCurrencyCode(),
    addCartSource,
  })
}

export const basketShowEvent = (action, items) => {
  const currency = getCurrencyCode()
  const value = getGA4Price(action.totalValue) || 0
  const valueWithTax = getGA4PriceWithTax(action.totalValue) || 0
  const products = getProductsGA4(items, EventCategory.ORDER, action.token)

  dataLayerPush<EcommerceBasketShowGA4DataLayer>({
    event: "view_cart",
    event_type: "ecommerce",
    ecommerce: {
      currency,
      value,
      items: products,
    },
  })

  window.jentisDataLayer?.pushCartViewEventAndItems({
    items: getJentisConvertedProductsItems(products),
    value,
    valueWithTax,
    currency,
  })
}

export const orderCheckoutSummaryEvent = (action, basketOrderedItems) => {
  const items = getProductsGA4(
    basketOrderedItems,
    EventCategory.ORDER,
    action.token
  )
  const value = getGA4Price(action.totalValue) || 0
  const valueWithTax = getGA4PriceWithTax(action.totalValue) || 0
  const currency = getCurrencyCode()

  dataLayerPush<EcommerceBasketBeginCheckoutGA4DataLayer>({
    event: "begin_checkout",
    event_type: "ecommerce",
    ecommerce: {
      currency: currency,
      value: value,
      items: items,
    },
  })

  window.jentisDataLayer?.pushBeginCheckoutEventAndItems({
    items: getJentisConvertedProductsItems(items),
    value,
    valueWithTax,
    currency,
  })
}

export const changePaymentMethod = (
  value: number,
  payment_type: number,
  system_id: string,
  basketOrderedItems: (BasketProductItem | BasketViewItem)[],
  cart_id: string
) => {
  const products = getProductsGA4(
    basketOrderedItems,
    EventCategory.ORDER,
    cart_id
  )
  const price = getGA4Price(value) || 0
  const priceWithTax = getGA4PriceWithTax(value) || 0
  const currency = getCurrencyCode()

  dataLayerPush<EcommerceBasketChangePaymentGA4DataLayer>({
    event: "add_payment_info",
    event_type: "ecommerce",
    ecommerce: {
      currency,
      value: price,
      payment_type,
      system_id,
      items: products,
    },
  })

  window.jentisDataLayer?.pushAddPaymentInfoEventAndItems({
    items: getJentisConvertedProductsItems(products),
    value: price,
    valueWithTax: priceWithTax,
    payment_type,
    system_id,
    currency,
  })
}

export const selectItemEvent = (action, priceValue: string | undefined) => {
  dataLayerPush<EcommerceSelectItemGA4DataLayer>({
    event: "select_item",
    event_type: "ecommerce",
    ecommerce: {
      items: [
        {
          item_id: action.saleConfigurations[0].id,
          item_name: action.name,
          currency: getCurrencyCode(),
          price: priceValue ? parseFloat(priceValue.replace(",", ".")) : null,
          index: null,
          quantity: "1",
          affiliation: globalHistory.location.hostname
            ? globalHistory.location.hostname
            : undefined,
          ...(action.saleConfigurations[0]?.flatData.defaultCloudBluePlan[0]
            ?.flatData.periods[0]?.period_name && {
            item_variant:
              action.saleConfigurations[0].flatData.defaultCloudBluePlan[0]
                .flatData.periods[0].period_name,
          }),
        },
      ],
    },
  })
}

export const selectDomainEvent = (
  periods: CloudBluePeriod[],
  priceValue: string,
  title?: string
) => {
  dataLayerPush<EcommerceSelectDomainGA4DataLayer>({
    event: "select_item",
    event_type: "ecommerce",
    ecommerce: {
      items: [
        {
          item_id: periods[0].id,
          item_name: title ? title : undefined,
          currency: getCurrencyCode(),
          price: parseFloat(priceValue.replace(",", ".")),
          item_category: "domain",
          item_variant: periods[0].period_name,
          index: null,
          quantity: "1",
          affiliation: globalHistory.location.hostname,
        },
      ],
    },
  })
}

export const productBoxClickEvent = ({
  productbox_type,
  productbox_option,
  productbox_checked_item,
  productbox_time,
  productbox_package,
}: Omit<ProductBoxClickEvent, "event_type" | "event">): void => {
  dataLayerPush<ProductBoxClickEvent>({
    event: "productbox_click",
    event_type: "productbox_interaction",
    productbox_type: productbox_type,
    productbox_option: productbox_option,
    productbox_checked_item: productbox_checked_item,
    productbox_time: productbox_time,
    productbox_package: productbox_package,
  })
}

export function sendPremiumButtonEvent(domainName: string): void {
  dataLayerPush<CmsObjectEvent>({
    event: "cms_object",
    event_type: "ux_interaction",
    object_category: "przejdz_na_gielde_domen",
    object_name: domainName,
  })

  window.jentisDataLayer?.pushCmsObjectEvent(
    "przejdz_na_gielde_domen",
    domainName
  )
}

export function sendAiSearchButtonEvent(value: string): void {
  dataLayerPush<AiSearchEvent>({
    event: "site_search_result",
    event_type: "site_ai_search_interaction",
    search_term: value,
    status: "ai",
  })
}

export function sendAiSearchExamplePromptEvent(value: string): void {
  dataLayerPush<CmsObjectEvent>({
    event: "cms_object",
    event_type: "ux_interaction",
    object_category: "ai_search_button",
    object_name: value,
  })

  window.jentisDataLayer?.pushCmsObjectEvent("ai_search_button", value)
}

export function sendFilterCategoryClickEvent(eventLabel?: string) {
  dataLayerPush<CmsObjectEvent>({
    event: "cms_object",
    event_type: "ux_interaction",
    object_category: EventCategory.DOMAIN_SEARCH_FILTERS,
    object_name: eventLabel || "",
  })

  window.jentisDataLayer?.pushCmsObjectEvent(
    EventCategory.DOMAIN_SEARCH_FILTERS,
    eventLabel || ""
  )
}

export function sendCostCalculatorModalButtonClick(): void {
  dataLayerPush<CmsObjectEvent>({
    event: "cms_object",
    event_type: "ux_interaction",
    object_category: "costs_calculator_start",
    object_name: "costs_calculator_start",
  })

  window.jentisDataLayer?.pushCmsObjectEvent(
    "costs_calculator_start",
    "costs_calculator_start"
  )
}

export function sendCostCalculatorClickConfig(label: string): void {
  dataLayerPush<CmsObjectEvent>({
    event: "cms_object",
    event_type: "ux_interaction",
    object_category: "costs_calculator_config",
    object_name: label,
  })

  window.jentisDataLayer?.pushCmsObjectEvent("costs_calculator_config", label)
}

export function sendExperimentEvent(experimentName: string, variant: string) {
  dataLayerPush<ExperimentEvent>({
    event: "ab_test_status",
    ab_test_name: experimentName,
    ab_test_variant: variant,
  })
}

export function sendConfigDrawerCloseEvent(object_category: string): void {
  dataLayerPush<CmsObjectEvent>({
    event: "cms_object",
    event_type: "ux_interaction",
    object_category,
    object_name: "exit_button",
  })

  window.jentisDataLayer?.pushCmsObjectEvent(object_category, "exit_button")
}

export const sendConfigDrawerPeriodBoxClickEvent = (
  periodName: string,
  productName: string
): void => {
  dataLayerPush<ProductBoxClickEvent>({
    event: "productbox_click",
    event_type: "productbox_interaction",
    productbox_type: "select",
    productbox_option: `drawer_period_${periodName}_${productName}`,
  })
}

export const basketAddPromoCodeEvent = (result: BasketResponse) => {
  const token = result.token || unknownTokenText

  dataLayerPush<EcommerceAddPromoCodeGA4DataLayer>({
    event: BasketActions.BASKET_ADD_PROMO_CODE,
    event_type: "ecommerce",
    ecommerce: {
      value: getGA4Price(result.total_net_price) || 0,
      currency: getCurrencyCode(),
      items: getProductsGA4(result.items, EventCategory.ORDER, token),
      savings: result.savings,
    },
  })

  pushOAOAddVoucher(token, result)
}

export const basketRemovePromoCodeEvent = (result: BasketResponse) => {
  const token = result.token || unknownTokenText

  const basketItems = getProductsGA4(result.items, EventCategory.ORDER, token)

  dataLayerPush<EcommerceRemovePromoCodeGA4DataLayer>({
    event: BasketActions.BASKET_REMOVE_PROMO_CODE,
    event_type: "ecommerce",
    ecommerce: {
      value: getGA4Price(result.total_net_price) || 0,
      currency: getCurrencyCode(),
      items: basketItems,
    },
  })

  pushOAORemoveVoucher(token, result.status)
}

export function sendAccountNumberReloadClick(): void {
  dataLayerPush<CmsObjectEvent>({
    event: "cms_object",
    event_type: "ux_interaction",
    object_category: "account_number_reload",
    object_name: "account_number_reload",
  })

  window.jentisDataLayer?.pushCmsObjectEvent(
    "account_number_reload",
    "account_number_reload"
  )
}
